import { useState, useEffect, useRef, useCallback } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppSelector, useAppDispatch, DriveRequest, PermissionSetType, CollectionFilesyncAPI, DriveErrorMessages, MessageHandlerDisplayType, useSendWatchRequestMutation,
  Message, getEnumKey, NodeIdentifier, ErrorStackItem, Snapshot, useGetPermissions, PermissionSetTypeMap, MaintainerGrantsMap, Directory, SearchNode, GrantSet, GrantSetType,
  useGetGrants, COLLECTION_PROTOCOL_VERSIONS, SnapshotData, NodeCollectionIdentifier, DriveSearchLimits, ACLTreeMap, SearchClient, isDisjointedACLNode
} from "src/common";
import { uiActions, DriveCallbackAsyncFunction } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

// Description: Return the entire state of the collection in a single response message.
// https://github.com/PreVeil/core/blob/dev/collection_server/docs/filesync.md#snapshot
export function useSearchSnapshotNodes(current_account: Account, search_client: SearchClient) {
  const default_permissions: PermissionSetType[] = useAppSelector((state: RootState) => state.drive.default_permissions);
  const default_grants = useAppSelector((state: RootState) => state.drive.default_grants);
  const [snapshot_data, setSnapshotData] = useState<SnapshotData | undefined>();
  const [error, setError] = useState<boolean>(false);
  const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);
  const { grants, getGrants, getAclKeyHistory, destroyGrants, error: grants_error } = useGetGrants(current_account);
  const [sendRequest] = useSendWatchRequestMutation();
  const dispatch = useAppDispatch();
  const page_number_ref = useRef<number>(0); // Hold the snapshot "page number"
  const node_identifiers_ref = useRef<NodeCollectionIdentifier>();
  const node_queue_ref = useRef<FSMessage.Dir[]>([]);
  const working_node_ref = useRef<FSMessage.Dir>();

  // NOTE: Cache acl tree for future batch pagination by sequences
  const acl_tree_map_ref = useRef<ACLTreeMap>(new Map<string, FSMessage.Dir[]>);
  function setACLTreeMapRef(collection_id: string, _acl_tree: FSMessage.Dir[]) {
    acl_tree_map_ref.current.set(collection_id, _acl_tree);
  }
  function getACLTreeMapRef(collection_id: string): FSMessage.Dir[] | undefined {
    return acl_tree_map_ref.current.get(collection_id);
  }

  // NOTE: Permissions stored for reuse
  const permission_sets_ref = useRef<PermissionSetTypeMap>(new Map<string, PermissionSetType>);
  function setPermissionSetsRef(collection_id: string, permission_set: PermissionSetType) {
    permission_sets_ref.current.set(collection_id, permission_set);
  }
  function getPermissionSetRef(collection_id: string): PermissionSetType | undefined {
    return permission_sets_ref.current.get(collection_id);
  }

  // Description: Maintainer nodes fetched grants
  const maintainer_grants_ref = useRef<MaintainerGrantsMap>(new Map<string, GrantSetType>);
  function setMaintainerGrantsRef(id: string, maintainer_grants: GrantSetType) {
    maintainer_grants_ref.current.set(id, maintainer_grants);
  }
  function getMaintainerGrantsRef(id: string): GrantSetType | undefined {
    return maintainer_grants_ref.current.get(id);
  }

  useEffect(() => {
    if (!!permissions && !!node_identifiers_ref.current) {
      setPermissionSetsRef(permissions.collection_id.B64(), permissions);
      destroyPermissions(); // NOTE: Return the permission state to undefined to be able to detect future changes
      !!node_identifiers_ref.current.seq ? handleInitFetchNode(node_identifiers_ref.current, permissions) :
        handlePreflightCheck(node_identifiers_ref.current, permissions);
    }
  }, [permissions]);

  // Description: Add the missing reader grants to the active grants object 
  useEffect(() => {
    if (!!grants) {
      if (!!node_identifiers_ref.current) {
        setMaintainerGrantsRef(grants.id, grants);
        getNext();
      } else {
        handleItemError(grants.id);
      }
      destroyGrants();
    }

    // LABEL GRANTS AS ERRORED - 
    grants_error && handleItemError(node_identifiers_ref.current?.id || "N/A");
  }, [grants, grants_error]);

  // Description: Run a quick check on the parent collection to see if its already stored in db
  async function handlePreflightCheck(_node_identifiers: NodeCollectionIdentifier, _permissions: PermissionSetType) {
    const id = _node_identifiers.id;
    const collection_id = _node_identifiers.collection_id;
    const drive_request = await CollectionFilesyncAPI.fetchSnapshot(current_account, _permissions, collection_id, id, 0, 0, 1, true);
    async function callback(message: FSMessage) {
      const response_snapshot = message.getSnapshot();
      if (message.getStatus() === FSStatus.OK && !!response_snapshot) {
        const response_snapshot = message.getSnapshot();
        const sequence = response_snapshot?.getSeq() || 0;
        const search_row = await search_client.getSearchByNodeIdentifiers(collection_id, id);
        // NOTE: if no changes then processed to success - next in queue in parent hook (search)
        (sequence !== search_row?.sequence) ?
          handleInitFetchNode(_node_identifiers, _permissions) :
          handleSuccess({ search_nodes: [], links: [], has_more: false });
      } else {
        handleInitFetchNode(_node_identifiers, _permissions);
      }
    }
    handleSendRequest(drive_request, callback, DriveErrorMessages.error_checking_snapshot_sequence);
  }

  //  Description: Initialize fetching snapshot pages
  async function handleInitFetchNode(_node_identifiers: NodeCollectionIdentifier, _permissions: PermissionSetType) {

    _node_identifiers.collection_protocol_version === COLLECTION_PROTOCOL_VERSIONS.V2 ?
      handleGetACLTree(_node_identifiers, _permissions) :
      handleFetchSnapshot(_node_identifiers, _permissions, _node_identifiers.seq, _node_identifiers.subseq);
  }

  // Description: Get V2 collection ACL Tree to collect grants
  async function handleGetACLTree(_node_identifiers: NodeCollectionIdentifier, _permissions: PermissionSetType) {
    const collection_id = _node_identifiers.collection_id;
    const existing_acl_tree = getACLTreeMapRef(collection_id);
    async function callback(message: FSMessage) {
      if (message.getStatus() === FSStatus.OK) {
        const acl_tree = message.getAclTree()?.getDirsList();
        if (!!acl_tree) {
          setACLTreeMapRef(collection_id, acl_tree.slice());
          node_queue_ref.current = acl_tree;
          getNext();
        } else {
          handleFetchSnapshot(_node_identifiers, _permissions, _node_identifiers.seq, _node_identifiers.subseq);
        }
      } else {
        handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_acl_tree }, message);
      }
    }

    // NOTE: Fetch getACLTreeRequest only if its not cached;
    if (!!existing_acl_tree) {
      node_queue_ref.current = existing_acl_tree.slice();
      getNext();
    } else {
      const request = await CollectionFilesyncAPI.getACLTreeRequest(collection_id);
      const drive_request = await DriveRequest.initializeRequest(request, current_account, _permissions);
      handleSendRequest(drive_request, callback, DriveErrorMessages.error_fetching_acl_tree);
    }
  }

  // Description: Bulk update permissions
  async function handleFetchSnapshot(_node_identifiers: NodeIdentifier, _permissions: PermissionSetType, seq: number = 0, subseq: number = 0) {
    const drive_request = await CollectionFilesyncAPI.fetchSnapshot(current_account, _permissions,
      _node_identifiers.collection_id, _node_identifiers.id, seq, subseq, DriveSearchLimits.SNAPSHOT_LIMIT, true, true);
    page_number_ref.current++;
    async function callback(message: FSMessage) {
      if (message.getStatus() === FSStatus.OK) {
        const response_snapshot = message.getSnapshot();
        if (!!response_snapshot) {
          const fetch_more = response_snapshot.hasHasMore() && page_number_ref.current < DriveSearchLimits.SNAPSHOT_PAGE_BATCH;
          if (fetch_more) {
            handleFetchSnapshot(_node_identifiers, _permissions, response_snapshot.getCursorSeq(), response_snapshot.getCursorSubseq());
          }
          processIndexedSnapshot(response_snapshot, _node_identifiers, _permissions, fetch_more);
        } else {
          handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_snapshot }, message);
        }
      } else {
        handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_snapshot }, message);
      }
    }
    handleSendRequest(drive_request, callback, DriveErrorMessages.error_checking_snapshot_sequence);
  }

  // Description: Parse Data into FSMessage.Dir[]
  // NOTES: I have to use entries because that is where we get the names from.
  //        Some entries will come back as FSMessage.Dir because the array is a flat representation
  //        Need to address the  indexed.links for additional Branches and Calls to handleFetchSnapshot
  async function processIndexedSnapshot(_snapshot: FSMessage.Snapshot, _node_identifiers: NodeIdentifier, _permission_set: PermissionSetType, fetch_more: boolean) {
    const _collection_id = _node_identifiers.collection_id;
    const _id = _node_identifiers.id;
    const indexed = Snapshot.getIndexedSnapshot(_snapshot);
    const links = Array.from(indexed.links.values());
    const directories = Array.from(indexed.dirs.values());
    const has_more = _snapshot.hasHasMore();
    const sequences = !fetch_more && has_more ? [_snapshot.getCursorSeq() || 0, _snapshot.getCursorSubseq() || 0] : undefined;
    // NOTE: Decrypt Entries and return to parent hook
    const search_nodes = _.flatten(await Promise.all(_.map(directories, async (dir: FSMessage.Dir) => {
      const maintainer_id = dir.getMaintainerId_asB64();
      const grants = getMaintainerGrantsRef(maintainer_id);
      const key_version = dir.getWrappedDirKey()?.getKeyVersion();
      const grant = !!grants ? GrantSet.getGrantByRole(grants, key_version) : undefined;
      const directory = await Directory.init(_collection_id, dir, current_account, _permission_set, grant, grants);
      const _search_nodes = !!directory ? (await SearchNode.parseDirectoryToSearchNodes(directory)).search_nodes : [];
      if (isDisjointedACLNode(dir)) {
        const acl_nodes = await SearchNode.parseDisjointedACLNodes(_collection_id, dir, current_account, grants);
        !!acl_nodes && _search_nodes.push(acl_nodes);
      }
      return _search_nodes;
    })));
    // NOTE: Add snapshot to db.search
    search_client.addItemToSearchIndex(_collection_id, _id, _snapshot);
    handleSuccess({ search_nodes, links, has_more, sequences });
  }

  // Description: Handle success for fetching snapshot - returns entries and children Link items to fetch
  function handleSuccess(_snapshot_data: SnapshotData) {
    setSnapshotData(_snapshot_data);
    setError(false);
  }

  // Description: Move to next directory item in queue or fetch snapshot after all grants are set
  function getNext() {
    working_node_ref.current = node_queue_ref.current.shift();
    const collection_id = node_identifiers_ref.current?.collection_id;
    const _permissions = !!collection_id ? getPermissionSetRef(collection_id) : undefined;
    if (!!node_identifiers_ref.current && !!collection_id && !!_permissions) {
      if (!!working_node_ref.current) {
        const dir = working_node_ref.current;
        const maintainer_id = dir.getMaintainerId_asB64();
        const existing_grants = getMaintainerGrantsRef(maintainer_id);
        // NOTE: Skip getting grants if already fetched and cached
        if (!!existing_grants) {
          getNext();
        } else {
          const acl_list = dir.getAclList();
          const current_grants = !!collection_id ? GrantSet.getCurrentUsersGrantSets(collection_id, maintainer_id, acl_list, current_account.user_id) : null;
          if (!!current_grants) {
            setMaintainerGrantsRef(maintainer_id, current_grants);
            const grant = GrantSet.getGrantByRole(current_grants);
            // IF we have all the grants move to next
            !!grant ? getNext() :
              getAclKeyHistory(maintainer_id, collection_id, default_grants, _permissions, GrantSet.init(current_grants));
          } else {
            getGrants(maintainer_id, collection_id, default_permissions, default_grants);
          }
        }
      } else {
        handleFetchSnapshot(node_identifiers_ref.current, _permissions, node_identifiers_ref.current.seq, node_identifiers_ref.current.subseq);
      }
    } else {
      handlePageErrorMessage("can't proceed into handleFetchSnapshot. Either node_identifiers, permissions or collection_id are undefined");
    }
  }
  // ----------------------------------------------------------------------------------------------------
  // Description: Callbacks and general calls
  // ----------------------------------------------------------------------------------------------------
  // Description: Send request handles error when the request object is null
  function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>, stack_error: string = DriveErrorMessages.error_sending_request) {
    if (!!request) {
      request.setCallback(callback);
      sendRequest(request);
    } else {
      handlePageErrorMessage(DriveErrorMessages.default, { stack_error, stack_message: DriveErrorMessages.error_sending_request });
    }
  }

  // Description: QUEUE error and move past
  function handleItemError(id: string) {
    dispatch(uiActions.handleRequestErrors(
      new Message("handleItemError: problems with getting snapshot. Missing grants or maintainer id",
        MessageHandlerDisplayType.logger),
      { stack_message: `item failed: ${id}` }));
    getNext();
  }

  // Description: Handle error message and send error to store
  function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null,
    display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
    const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
    const stack = new ErrorStackItem("[useSearchSnapshotNodes Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
    dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
    setError(true);
  }

  // Description: Reset all state for internal and external clean up
  function reset() {
    page_number_ref.current = 0;
    node_identifiers_ref.current = undefined;
    node_queue_ref.current = [];
    working_node_ref.current = undefined;
    setError(false);
    setSnapshotData(undefined);
    destroyPermissions();
    destroyGrants();
  }

  // Description: Fetch Snapshot and decrypt entry names
  const fetchSnapshot = useCallback((
    collection_id: string,
    id: string = "",
    collection_protocol_version: COLLECTION_PROTOCOL_VERSIONS = COLLECTION_PROTOCOL_VERSIONS.V2,
    sequences?: number[]) => {
    node_identifiers_ref.current = {
      collection_id,
      id,
      collection_protocol_version,
      seq: !!sequences ? sequences[0] : 0,
      subseq: !!sequences ? sequences[1] : 0
    };
    page_number_ref.current = 0;
    getPermissions(collection_id, default_permissions);
  }, []);


  // Description: Reset the snapshot hook
  const destroySnapshot = useCallback(() => {
    reset();
  }, []);

  return {
    fetchSnapshot,
    destroySnapshot,
    snapshot_data,
    error
  };
}